import React, { useState, useRef } from "react";
import {
  User,
  Loader2,
  ImagePlus,
  X,
  ExternalLink,
  Heart,
  MessageSquare,
  Repeat,
  Flag,
  Trash2,
} from "lucide-react";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../firebase";
import { useAppContext } from "../context/AppContext";

// ✨ Міні-компонент для розумного приховування довгого тексту
function PostText({ text }: { text: string }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return null;

  const maxLength = 150;
  // Рахуємо кількість переносів рядка
  const lines = text.split("\n");
  const isTooLong = text.length > maxLength || lines.length > 4;

  if (!isTooLong) {
    return (
      <p className="text-sm text-[var(--text-main)] mt-2 whitespace-pre-wrap break-all leading-relaxed w-full overflow-hidden">
        {text}
      </p>
    );
  }

  // Робимо правильне урізання тексту для прев'ю
  let previewText = text;
  if (lines.length > 4) {
    previewText = lines.slice(0, 4).join("\n"); // беремо лише перші 4 рядки
  }
  if (previewText.length > maxLength) {
    previewText = previewText.substring(0, maxLength); // беремо лише перші 150 символів
  }

  return (
    <div className="mt-2 w-full overflow-hidden flex flex-col items-start">
      <p className="text-sm text-[var(--text-main)] whitespace-pre-wrap break-all leading-relaxed w-full">
        {isExpanded ? text : `${previewText}...`}
      </p>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
        className="text-[#a073e7] font-bold mt-1 hover:underline text-xs"
      >
        {isExpanded ? "Сховати" : "Читати далі..."}
      </button>
    </div>
  );
}

export default function FeedTab({
  safePosts,
  allUsers,
  setSelectedChatUser,
  setCommentPostId,
  handleReport,
}: any) {
  const { lang, user, activePet } = useAppContext();

  const [postInput, setPostInput] = useState("");
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<"image" | "video" | null>(null);
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  const [lastPostTime, setLastPostTime] = useState(0);

  const mediaRef = useRef<HTMLInputElement>(null);

  // ✨ Визначаємо адмінів за UIDs, точно як у правилах Firestore
  const adminUids = [
    "uhTrLUhzCjallqNDIof3JudJr1p2",
    "vl1DV8Azfmhz0xWznwnYJC37LBi2",
  ];
  const isAdmin = user?.uid && adminUids.includes(user.uid);

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024)
      return alert(
        lang === "uk"
          ? "Файл занадто великий (макс. 50MB)"
          : "File too large (max 50MB)"
      );
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
    setMediaType(file.type.startsWith("video/") ? "video" : "image");
  };

  const handleSendPost = async () => {
    if (!postInput.trim() && !mediaFile) return;
    if (Date.now() - lastPostTime < 15000) {
      alert(
        lang === "uk"
          ? "⏳ Зачекайте 15 секунд перед наступною публікацією!"
          : "⏳ Please wait 15 seconds before posting again!"
      );
      return;
    }
    setIsMediaUploading(true);
    let finalMediaUrl = null;
    let finalMediaType = mediaType;
    try {
      if (mediaFile && user) {
        const storage = getStorage();
        const fileExt = mediaFile.name.split(".").pop();
        const fileName = `feed_media/${Date.now()}_${user.uid}.${fileExt}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, mediaFile);
        finalMediaUrl = await getDownloadURL(storageRef);
      }
      await addDoc(collection(db, "posts"), {
        type: "user_post",
        authorId: user?.uid,
        authorName: activePet.name,
        authorPhoto: activePet.photo || null,
        authorBreed: activePet.breed || "",
        authorGender: activePet.gender || "",
        authorDob: activePet.dob || "",
        text: postInput,
        mediaUrl: finalMediaUrl,
        mediaType: finalMediaType,
        likes: [],
        comments: [],
        commentsCount: 0,
        repostsCount: 0,
        createdAt: serverTimestamp(),
      });
      setLastPostTime(Date.now());
      setPostInput("");
      setMediaFile(null);
      setMediaPreview(null);
      setMediaType(null);
    } catch (error) {
      console.error(error);
      alert("Помилка завантаження файлу.");
    } finally {
      setIsMediaUploading(false);
    }
  };

  const handleToggleLike = async (
    postId: string,
    currentLikes: string[] = []
  ) => {
    if (!user) return;
    const postRef = doc(db, "posts", postId);
    if (currentLikes.includes(user.uid)) {
      await updateDoc(postRef, {
        likes: currentLikes.filter((id) => id !== user.uid),
      });
    } else {
      await updateDoc(postRef, { likes: [...currentLikes, user.uid] });
    }
  };

  const handleRepost = async (post: any) => {
    if (!user) return;
    if (post.authorId === user.uid)
      return alert(
        lang === "uk"
          ? "Ви не можете репостити власний запис!"
          : "Cannot repost your own post!"
      );
    if (
      window.confirm(
        lang === "uk" ? "Зробити репост цього запису?" : "Repost this?"
      )
    ) {
      const postRef = doc(db, "posts", post.id);
      await updateDoc(postRef, { repostsCount: (post.repostsCount || 0) + 1 });
      await addDoc(collection(db, "posts"), {
        type: "user_post",
        authorId: user.uid,
        authorName: activePet.name,
        authorPhoto: activePet.photo || null,
        authorBreed: activePet.breed || "",
        authorGender: activePet.gender || "",
        authorDob: activePet.dob || "",
        text: post.text || "",
        mediaUrl: post.mediaUrl || null,
        mediaType: post.mediaType || null,
        isRepost: true,
        originalAuthorName: post.authorName,
        likes: [],
        comments: [],
        commentsCount: 0,
        repostsCount: 0,
        createdAt: serverTimestamp(),
      });
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (
      window.confirm(
        lang === "uk" ? "Видалити цей запис?" : "Delete this post?"
      )
    ) {
      await deleteDoc(doc(db, "posts", postId));
    }
  };

  return (
    <>
      <div className="bg-[var(--bg-card)] p-4 rounded-2xl border border-[var(--border-color)] shadow-sm flex flex-col gap-3">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-full bg-[var(--accent-soft)] overflow-hidden shrink-0 border border-[var(--bg-card)] flex items-center justify-center text-[#a073e7]">
            {activePet.photo ? (
              <img
                src={activePet.photo}
                className="w-full h-full object-cover"
              />
            ) : (
              <User size={20} />
            )}
          </div>
          <textarea
            value={postInput}
            onChange={(e) => setPostInput(e.target.value)}
            placeholder={
              lang === "uk"
                ? "Що нового у вашого хвостика?"
                : "What's new with your pet?"
            }
            className="flex-1 bg-transparent resize-none outline-none text-sm text-[var(--text-main)] min-h-[60px] pt-1"
          />
        </div>
        {isMediaUploading && (
          <div className="text-xs text-[#a073e7] flex items-center gap-2 font-bold">
            <Loader2 size={14} className="animate-spin" /> Завантаження у
            хмару...
          </div>
        )}
        {mediaPreview && (
          <div className="relative rounded-xl overflow-hidden max-h-[200px] border border-[var(--border-color)] bg-[var(--bg-soft)] flex justify-center items-center">
            {mediaType === "image" ? (
              <img
                src={mediaPreview}
                className="max-h-[200px] object-contain"
              />
            ) : (
              <video src={mediaPreview} controls className="max-h-[200px]" />
            )}
            <button
              onClick={() => {
                setMediaFile(null);
                setMediaPreview(null);
                setMediaType(null);
              }}
              className="absolute top-2 right-2 bg-black/60 p-1.5 rounded-full text-white active:scale-95"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <div className="flex justify-between items-center border-t border-[var(--border-color)] pt-3 mt-1">
          <button
            className="text-[var(--text-muted)] p-2 hover:bg-[var(--bg-soft)] rounded-full transition-all active:scale-95"
            onClick={() => mediaRef.current?.click()}
          >
            <ImagePlus size={18} />
          </button>
          <input
            ref={mediaRef}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={handleMediaChange}
          />
          <button
            onClick={handleSendPost}
            disabled={isMediaUploading}
            className="px-5 py-2 bg-[var(--inverted-btn)] text-[var(--inverted-text)] rounded-xl font-bold text-xs active:scale-95 transition-all shadow-md disabled:opacity-50"
          >
            {lang === "uk" ? "Опублікувати" : "Post"}
          </button>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        {safePosts.length === 0 && (
          <div className="text-center py-10 text-[var(--text-muted)] font-bold text-sm">
            Тут поки порожньо. Будьте першим!
          </div>
        )}
        {safePosts.map((post: any) => {
          const isLiked = post.likes?.includes(user?.uid);
          const authorFollowersCount = allUsers.filter((u: any) =>
            u.following?.includes(post.authorId)
          ).length;
          return (
            <div
              key={post.id}
              className="bg-[var(--bg-card)] p-4 rounded-2xl border border-[var(--border-color)] shadow-sm flex flex-col gap-2 animate-fade-in relative w-full overflow-hidden"
            >
              {post.isSponsored && (
                <div className="text-[9px] font-black text-[#a073e7] bg-[var(--accent-soft)] border border-[var(--border-accent)] px-2 py-0.5 rounded-md w-fit mb-1 uppercase tracking-wide">
                  Реклама
                </div>
              )}
              {post.isRepost && (
                <div className="text-[10px] font-bold text-[var(--text-muted)] flex items-center gap-1 mb-1">
                  <Repeat size={12} className="text-[#10b981]" />{" "}
                  {lang === "uk"
                    ? `Репост від ${post.originalAuthorName}`
                    : `Reposted from ${post.originalAuthorName}`}
                </div>
              )}
              <div className="flex justify-between items-start w-full">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full bg-[var(--accent-soft)] overflow-hidden shrink-0 border border-[var(--bg-card)] flex items-center justify-center cursor-pointer text-[#a073e7]"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedChatUser({
                        id: post.authorId,
                        name: post.authorName,
                        photo: post.authorPhoto,
                        breed: post.authorBreed,
                        gender: post.authorGender,
                        dob: post.authorDob,
                      });
                    }}
                  >
                    {post.authorPhoto ? (
                      <img
                        src={post.authorPhoto}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User size={20} />
                    )}
                  </div>
                  <div className="overflow-hidden">
                    <h4 className="font-bold text-sm text-[var(--text-main)] truncate max-w-[180px]">
                      {post.authorName}
                    </h4>
                    <span className="text-[10px] text-[var(--text-muted)]">
                      {post.createdAt?.toDate().toLocaleString()}{" "}
                      {post.authorId &&
                        !post.isSponsored &&
                        `• ${authorFollowersCount} ${
                          lang === "uk" ? "підписників" : "followers"
                        }`}
                    </span>
                  </div>
                </div>
              </div>

              {/* ✨ РОЗУМНИЙ ТЕКСТ ПОСТА */}
              <PostText text={post.text} />

              {post.mediaUrl && (
                <div className="rounded-xl overflow-hidden border border-[var(--border-color)] bg-[var(--bg-soft)] mt-2 flex justify-center w-full">
                  {post.mediaType === "image" ? (
                    <img
                      src={post.mediaUrl}
                      className="max-h-[300px] w-full object-cover"
                    />
                  ) : (
                    <video
                      src={post.mediaUrl}
                      controls
                      className="max-h-[300px] w-full"
                    />
                  )}
                </div>
              )}
              {post.isSponsored && post.link && (
                <a
                  href={post.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 w-full py-3 bg-[var(--inverted-btn)] text-[var(--inverted-text)] font-bold text-center rounded-xl active:scale-95 transition-all text-sm flex items-center justify-center gap-2 shadow-md"
                >
                  {post.actionText ||
                    (lang === "uk" ? "Перейти" : "Learn More")}{" "}
                  <ExternalLink size={16} />
                </a>
              )}
              <div className="flex items-center gap-6 mt-3 pt-3 border-t border-[var(--border-color)] relative">
                <button
                  onClick={() => handleToggleLike(post.id, post.likes)}
                  className={`flex items-center gap-1.5 text-xs font-bold transition-all active:scale-95 ${
                    isLiked
                      ? "text-[var(--red-text)]"
                      : "text-[var(--text-muted)]"
                  }`}
                >
                  <Heart
                    size={18}
                    fill={isLiked ? "currentColor" : "none"}
                    className={isLiked ? "animate-pulse" : ""}
                  />{" "}
                  {post.likes?.length || 0}
                </button>
                <button
                  onClick={() => setCommentPostId(post.id)}
                  className="flex items-center gap-1.5 text-xs font-bold text-[var(--text-muted)] transition-all active:scale-95 hover:text-[#a073e7]"
                >
                  <MessageSquare size={18} /> {post.commentsCount || 0}
                </button>
                <button
                  onClick={() => handleRepost(post)}
                  className="flex items-center gap-1.5 text-xs font-bold text-[var(--text-muted)] transition-all active:scale-95 hover:text-[#10b981]"
                >
                  <Repeat size={18} /> {post.repostsCount || 0}
                </button>
                <div className="ml-auto flex items-center gap-3">
                  {post.authorId !== user?.uid && (
                    <button
                      onClick={() =>
                        handleReport(
                          "post",
                          String(post.id),
                          post.authorId || ""
                        )
                      }
                      className="text-[var(--text-muted)] active:scale-95 hover:text-[var(--red-text)] transition-all"
                    >
                      <Flag size={18} />
                    </button>
                  )}

                  {/* ✨ Кнопка видалення: показується автору АБО адміністраторам */}
                  {(post.authorId === user?.uid || isAdmin) && (
                    <button
                      onClick={() => handleDeletePost(post.id)}
                      className="text-[var(--text-muted)] active:scale-95 hover:text-[var(--red-text)] transition-all"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
